import { Box } from '@mui/material';

import { Shimmer } from './Shimmer';

interface CardSkeletonProps {
  /** Number of placeholder lines in the body. */
  lines?: number;
  /** Fixed body height — use to match a chart card so the grid doesn't reflow. */
  bodyHeight?: number | string | Record<string, number | string>;
  /** Leading round block on each line, for avatar/icon lists. */
  withAvatars?: boolean;
}

/**
 * Generic card placeholder: title block, optional subtitle, then either a solid
 * body (charts) or a stack of lines (lists). Uses the same border, radius and
 * padding as the real cards so swapping to content doesn't shift the layout.
 */
export const CardSkeleton = ({ lines = 4, bodyHeight, withAvatars = false }: CardSkeletonProps) => (
  <Box
    sx={(theme) => ({
      bgcolor: 'white', border: `1px solid ${theme.palette.grey[200]}`, borderRadius: '16px',
      p: { xs: '16px', sm: '20px' },
      boxShadow: '0px 1px 3px rgba(0,0,0,0.06)',
      display: 'flex', flexDirection: 'column', gap: '16px',
    })}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Shimmer height={14} width="38%" />
      <Shimmer height={11} width="55%" />
    </Box>

    {bodyHeight ? (
      <Shimmer height={bodyHeight} radius="10px" />
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {Array.from({ length: lines }, (_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {withAvatars && <Shimmer height={32} width={32} radius="50%" />}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
              <Shimmer height={12} width={`${72 - i * 6}%`} />
              <Shimmer height={10} width={`${48 - i * 4}%`} />
            </Box>
            <Shimmer height={12} width={{ xs: 48, sm: 64 }} />
          </Box>
        ))}
      </Box>
    )}
  </Box>
);
